/**
 * MarkAsLostModal — captures loss reason + optional archive on mark-as-lost.
 *
 * §1.1 (2026-07-29) — Prior implementation used a bare `confirm()` dialog:
 * title + one-line message + [Cancel][Confirm]. D-10 spec requires:
 *   1. Loss reason capture (dropdown of canned reasons)
 *   2. "Also archive" checkbox (uses <Checkbox> atom)
 *   3. Explanatory body (what happens, not just a restated title)
 *
 * `quoteAPI.markAsLost(id, reason)` already accepts a reason as its second
 * argument (services/api.js:141) — this modal wires the missing UI.
 *
 * IDI-406: the loss-reason options are the dealer's Settings -> Lost Reasons
 * list (useSettings 'lost_reasons'), the same list Settings shows. For a
 * migrated dealer that is their legacy LostReasonTypes. The local set below
 * is only a fallback when settings have not loaded.
 */
import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Slideover';
import { Button } from '../ui/Button';
import SelectMenu from '../ui/SelectMenu';
import { useSettingsContext } from '../../hooks/useSettings';

// Fallback only, when settings have not loaded (no provider / no rows).
// IDI-406: the options are the dealer's Settings -> Lost Reasons list
// (migrated legacy reasons for migrated dealers, the seed set otherwise).
// Values persist as the reason LABEL via markAsLost(id, reason).
export const FALLBACK_LOSS_REASONS = [
  { value: 'price', label: 'Price too high' },
  { value: 'competitor', label: 'Went with a competitor' },
  { value: 'cancelled', label: 'Project cancelled' },
  { value: 'timing', label: 'Timing / not ready' },
  { value: 'no_response', label: 'No response from customer' },
  { value: 'scope_change', label: 'Scope changed' },
  { value: 'other', label: 'Other' },
];

export default function MarkAsLostModal({ open, onCancel, onSubmit }) {
  const { getOptions } = useSettingsContext();
  const tenantReasons = (getOptions('lost_reasons') || [])
    .map((o) => ({ value: String(o.value ?? o.id ?? o.label), label: o.label || o.name || String(o.value) }))
    .filter((o) => o.label);
  const LOSS_REASONS = tenantReasons.length ? tenantReasons : FALLBACK_LOSS_REASONS;
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  // §3al (2026-08-18): Lost ALWAYS archives. No user choice.
  // The prop shape is preserved (alsoArchive: true, always) so the caller
  // doesn't need to change its API — but there's no toggle to construct.
  const alsoArchive = true;
  const [submitting, setSubmitting] = useState(false);

  // Reset state when the modal opens (so re-opening on a different quote
  // doesn't inherit the previous entry).
  useEffect(() => {
    if (open) {
      setReason('');
      setNotes('');
      setSubmitting(false);
      // §3al: alsoArchive is now hardcoded true; no reset needed.
    }
  }, [open]);

  const canSubmit = reason !== '' && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    // Combine dropdown value + optional free-text notes into a single reason
    // string (backend field is a single `reason` per api.js:141). If the user
    // added notes, append them after a hyphen for later parsing/display.
    const label = LOSS_REASONS.find(r => r.value === reason)?.label || reason;
    const composedReason = notes.trim() ? `${label} — ${notes.trim()}` : label;
    await onSubmit({ reason: composedReason, alsoArchive });
    // Parent closes the modal; submitting stays true until unmount.
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="Mark as Lost?"
      width="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleSubmit} disabled={!canSubmit} loading={submitting}>
            Mark as Lost
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Body: describe what happens, not just restate the title. */}
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          This quote will be marked <strong>Lost</strong> and removed from your active
          pipeline. You can still find it under the <em>Lost</em> filter.
        </p>

        {/* Loss reason (required) */}
        <div>
          <label
            htmlFor="mark-lost-reason"
            className="block text-sm font-medium mb-1"
            style={{ color: 'var(--color-text-primary)' }}
          >
            Loss reason <span style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <SelectMenu
            id="mark-lost-reason"
            value={reason}
            onChange={setReason}
            options={LOSS_REASONS}
            placeholder="Select a reason…"
            width="full"
            disabled={submitting}
            aria-label="Loss reason"
          />
        </div>

        {/* Optional notes */}
        <div>
          <label
            htmlFor="mark-lost-notes"
            className="block text-sm font-medium mb-1"
            style={{ color: 'var(--color-text-primary)' }}
          >
            Additional notes <span style={{ color: 'var(--color-text-secondary)' }}>(optional)</span>
          </label>
          <textarea
            id="mark-lost-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Any context that will help future you (or a teammate) understand this decision."
            className="block w-full px-3 py-2 text-sm border rounded focus-ring"
            style={{
              backgroundColor: 'var(--color-surface)',
              borderColor: 'var(--color-border)',
              color: 'var(--color-text-primary)',
            }}
            disabled={submitting}
          />
        </div>

        {/* §3al (2026-08-18): auto-archive on Lost. The "Also archive"
            checkbox this replaced was optional; the ruling made it mandatory,
            so the checkbox is gone and the archive fires on every confirm.
            The user-facing message says as much. */}
        <div className="pt-2 border-t text-sm" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}>
          Marking this quote Lost also archives it, so it will no longer appear in the active list. You can still find it via the Archived filter.
        </div>
      </div>
    </Modal>
  );
}
